import { useCallback, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import useApiCall from '../../../../../../hooks/useApiCall';
import { ErrorsInterface } from '../../../../../../hooks/useErrors';
import useGetGeneralAppData from '../../../../../../hooks/useGetGeneralAppData';
import { useQuery } from '../../../../../../hooks/useQuery';
import WorkplacesService from '../../../../../../services/WorkplacesService';
import parseCurrencyStringToFloat from '../../../../../../utils/parseCurrencyStringToFloat';
import { TransfersBehaviorType } from '../../../../../Company/Settings/types';

interface UseFormSubmitI {
  name: string;
  workplaceCode: string;
  hasAffreightment: boolean;
  operator: { value: string, label: string };
  usesVtAndAffreightment: boolean;
  cep: string;
  streetName: string;
  number: number | string;
  complement: string;
  district: string;
  city: string;
  uf: string;
  lat: string | number;
  lng: string | number;
  errors: ErrorsInterface[];
  compareWithDesiredVtValue: boolean;
  desiredVtValue: string;
  walkDistance: number[];
  sameWalkDistanceFromCompany: boolean;
  sameDesiredVtValueFromCompany: boolean;
  integratesWithRiocard: boolean;
  kindOfVtSpTrans: 'common' | 'vt';
  hasKmLimit: boolean;
  kmLimit: number[];
  isActive: boolean;
  transfersBehavior: TransfersBehaviorType;
  companyTransfersBehavior: TransfersBehaviorType;
  sameTransfersBehaviorFromCompany: boolean;
}

export default function useFormSubmit({
  name,
  workplaceCode,
  hasAffreightment,
  operator,
  usesVtAndAffreightment,
  cep,
  streetName,
  number,
  complement,
  district,
  city,
  uf,
  lat,
  lng,
  errors,
  desiredVtValue,
  walkDistance,
  sameWalkDistanceFromCompany,
  sameDesiredVtValueFromCompany,
  integratesWithRiocard,
  kindOfVtSpTrans,
  hasKmLimit,
  kmLimit,
  isActive,
  transfersBehavior,
  companyTransfersBehavior,
  sameTransfersBehaviorFromCompany,
}: UseFormSubmitI) {
  const [isSubmitting, setIsSubmitting] = useState(false);

  const navigate = useNavigate();
  const { workplaceId } = useParams();
  const { apiCall } = useApiCall();
  const { userCode, userProfile, companyCode } = useGetGeneralAppData();
  const query = useQuery();
  const returnLink = query.get('returnLink');

  const transfersBehaviorMap: { [key in TransfersBehaviorType]: number } = {
    'unlimited': 0,
    'minimun': 1,
    'default': 2,
  };

  const createWorkplace = useCallback(async () => {
    await apiCall({
      onStartLoad: () => setIsSubmitting(true),
      onEndLoad: () => setIsSubmitting(false),
      apiToCall: WorkplacesService.createWorkplace,
      reqBody: JSON.stringify({
        codUsu: userCode,
        perfilUsu: userProfile,
        codEmpresa: companyCode,
        dadosEndereco: [{
          tipoEnd: 4,
          codEnd: 0,
          endAtivo: true,
          codEndNoclienteAlfaN: workplaceCode,
          cep: cep,
          logradouro: streetName.slice(0, 50),
          numero: number,
          complemento: complement.slice(0, 25),
          bairro: district.slice(0, 25),
          cidade: city.slice(0, 30),
          UF: uf.slice(0, 2),
          denominacaoLT: name.slice(0, 40),
          lat,
          lng,
          temFretamento: hasAffreightment,
          codFreta: Number(operator.value) ?? 0,
          opcaoVTFreta: hasAffreightment ? !usesVtAndAffreightment : false,
          dataCadEnd: new Date().toISOString(),
          dataAtualEnd: new Date().toISOString(),
          distancia: sameWalkDistanceFromCompany ? 0 : Number(walkDistance[1]),
          valorTeto: sameDesiredVtValueFromCompany ? 0 : parseCurrencyStringToFloat(desiredVtValue),
          semIntegrarRJ: !integratesWithRiocard,
          semTaxaSPtrans: kindOfVtSpTrans === 'common' ? true : false,
          temLimitadorKM: !!hasKmLimit,
          KM: Number(kmLimit[1]) ? (Number(kmLimit[1] * 1000)) : 0,
          tipoBaldeacao: transfersBehaviorMap[sameTransfersBehaviorFromCompany ? companyTransfersBehavior : transfersBehavior],
          tipoBaldeacaoEmp: 0,
        }]
      }),
      catchMessage: 'Não foi possível adicionar o local. Por favor, tente novamente',
      actionAfterResponse: (apiResponse) => {
        if (!apiResponse.codigo) {
          toast.error('Não foi possível gravar o local. Por favor, tente novamente');
          return;
        }
        toast.success(`O local ${name} foi adicionada com sucesso!`);
        navigate('/workplaces');
      },
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiCall, cep, city, companyCode, companyTransfersBehavior, complement, desiredVtValue, district, hasAffreightment, hasKmLimit, integratesWithRiocard, kindOfVtSpTrans, kmLimit, lat, lng, name, navigate, number, operator.value, sameDesiredVtValueFromCompany, sameTransfersBehaviorFromCompany, sameWalkDistanceFromCompany, streetName, transfersBehavior, uf, userCode, userProfile, usesVtAndAffreightment, walkDistance, workplaceCode]);


  const updateWorkplace = useCallback(async () => {
    console.log('upd');
    console.log(lat, lng);
    await apiCall({
      onStartLoad: () => setIsSubmitting(true),
      onEndLoad: () => setIsSubmitting(false),
      apiToCall: WorkplacesService.updateWorkplace,
      reqBody: JSON.stringify({
        codUsu: userCode,
        perfilUsu: userProfile,
        codEmpresa: companyCode,
        dadosEndereco: [{
          codEnd: Number(workplaceId),
          tipoEnd: 4,
          endAtivo: isActive,
          codEndNoclienteAlfaN: workplaceCode,
          cep: cep,
          logradouro: streetName.slice(0, 50),
          numero: number,
          complemento: complement.slice(0, 25),
          bairro: district.slice(0, 25),
          cidade: city.slice(0, 30),
          UF: uf.slice(0, 2),
          denominacaoLT: name.slice(0, 40),
          lat: Number(Number(String(lat).replaceAll(',', '.')).toFixed(6)),
          lng: Number(Number(String(lng).replaceAll(',', '.')).toFixed(6)),
          temFretamento: hasAffreightment,
          codFreta: Number(operator.value) ?? 0,
          opcaoVTFreta: hasAffreightment ? !usesVtAndAffreightment : false,
          dataCadEnd: new Date().toISOString(),
          dataAtualEnd: new Date().toISOString(),
          distancia: sameWalkDistanceFromCompany ? 0 : Number(walkDistance[1]),
          valorTeto: sameDesiredVtValueFromCompany ? 0 : parseCurrencyStringToFloat(desiredVtValue),
          semIntegrarRJ: !integratesWithRiocard,
          semTaxaSPtrans: kindOfVtSpTrans === 'common' ? true : false,
          temLimitadorKM: !!hasKmLimit,
          KM: Number(kmLimit[1]) ? (Number(kmLimit[1] * 1000)) : 0,
          tipoBaldeacao: transfersBehaviorMap[sameTransfersBehaviorFromCompany ? companyTransfersBehavior : transfersBehavior],
          tipoBaldeacaoEmp: 0,
        }]
      }),
      catchMessage: 'Não foi possível atualizar o local. Por favor, tente novamente',
      actionAfterResponse: (apiResponse) => {
        if (!apiResponse.codigo) {
          toast.error('Não foi possível atualizar o local. Por favor, tente novamente');
          return;
        }
        toast.success(`O local ${name} foi atualizado com sucesso!`);
        navigate(returnLink ? returnLink :'/workplaces');
      },
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiCall, cep, city, companyCode, companyTransfersBehavior, complement, desiredVtValue, district, hasAffreightment, hasKmLimit, integratesWithRiocard, isActive, kindOfVtSpTrans, kmLimit, lat, lng, name, navigate, number, operator.value, returnLink, sameDesiredVtValueFromCompany, sameTransfersBehaviorFromCompany, sameWalkDistanceFromCompany, streetName, transfersBehavior, uf, userCode, userProfile, usesVtAndAffreightment, walkDistance, workplaceCode, workplaceId]);

  const isFormValid =(workplaceCode && cep && streetName && district && city && uf && name && ((hasAffreightment && operator.value) || !hasAffreightment) && errors.length === 0);

  return {
    isFormValid,
    updateWorkplace,
    createWorkplace,
    isSubmitting,
  };
}
